import * as React from 'react';
import { useCallback, useContext, useEffect, useState } from 'react';

import { InlineNotification } from '@coursera/cds-core';

import { ProfileContext } from 'bundles/account-profile/components/recruiting-profile-viewer/ProfileContextProvider';
import PrivacySettingsDialog from 'bundles/account-profile/components/recruiting-profile-viewer/components/privacy-settings/PrivacySettingsDialog';
import EditProfileLinkDialog from 'bundles/account-profile/components/recruiting-profile-viewer/components/profile-and-invitation/EditProfileLinkDialog';
import PublicProfileShareLinkDialog from 'bundles/account-profile/components/recruiting-profile-viewer/components/profile-and-invitation/PublicProfileShareLinkDialog';
import { getCurrentSlug } from 'bundles/account-profile/components/recruiting-profile-viewer/utils/profileSlugUtils';
import { handleShareProfile } from 'bundles/account-profile/components/recruiting-profile-viewer/utils/utils';
import ErrorBoundaryWithTagging from 'bundles/common/components/ErrorBoundaryWithTagging';

import _t from 'i18n!nls/account-profile';

type PropsFromCaller = {
  isOpen: boolean;
  openPrivacySettingsDialog?: boolean;
  onClose: () => void;
  onShareFailure?: () => void;
};

const ShareProfileLinkDialog = ({ isOpen, openPrivacySettingsDialog, onClose, onShareFailure }: PropsFromCaller) => {
  const { profileState = {} } = useContext(ProfileContext);
  const { showToPublic } = profileState;
  const [initialShowToPublicValue, setInitialShowToPublicValue] = useState<boolean | undefined>(undefined);
  const [isShowToPublic, setIsShowToPublic] = useState<boolean>(showToPublic || false);
  const [isShowEdit, setShowEdit] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  // In the share profile flow, there is a case where a learner clicks the privacy settings button and we
  // want to ensure the privacy settings dialog opens first regardless if the profile is public or not
  const [mustOpenPrivacySettings, setMustOpenPrivacySettings] = useState<boolean | undefined>(
    openPrivacySettingsDialog
  );
  const [editMessage, setEditMessage] = useState<string>();

  const shareProfile = useCallback<(...args: $TSFixMe[]) => $TSFixMe>(
    () => handleShareProfile(showToPublic || false, profileState, undefined, onShareFailure),
    [profileState, onShareFailure, showToPublic]
  );

  useEffect(() => {
    if (showToPublic && !mustOpenPrivacySettings && !linkCopied) {
      shareProfile();
      setLinkCopied(true);
    }

    if (!initialShowToPublicValue && showToPublic !== null) setInitialShowToPublicValue(showToPublic);
  }, [initialShowToPublicValue, mustOpenPrivacySettings, shareProfile, showToPublic, linkCopied]);

  useEffect(() => {
    setIsShowToPublic(showToPublic || false);
  }, [showToPublic]);

  const handleClosePrivacySettingsDialog = (isShareToPublic?: boolean | null) => {
    setMustOpenPrivacySettings(false);

    // Only open share profile dialog after privacy settings dialog has closed if the showToPublic value
    // changes from unchecked to checked
    if (isShareToPublic && initialShowToPublicValue !== isShareToPublic) {
      shareProfile();
      setIsShowToPublic(true);
    } else {
      onClose();
    }
  };

  const currentSlug = getCurrentSlug(profileState);

  const handleEditClose = (opts: { closeDialog?: boolean; message?: string }) => {
    if (opts.message) {
      setEditMessage(opts.message);
    }
    if (opts.closeDialog) {
      onClose();
    } else {
      setShowEdit(false);
    }
  };

  return (
    <>
      {isOpen && (
        <div>
          {!mustOpenPrivacySettings && (
            <div>
              {isShowToPublic ? (
                (isShowEdit && (
                  <EditProfileLinkDialog isOpen={isOpen} onClose={handleEditClose} currentSlug={currentSlug} />
                )) || (
                  <PublicProfileShareLinkDialog
                    isOpen={isOpen}
                    onClose={onClose}
                    currentSlug={currentSlug}
                    slugCount={profileState?.slugs?.length || 0}
                    message={editMessage}
                    onEditClick={() => setShowEdit(true)}
                  />
                )
              ) : (
                <PrivacySettingsDialog isOpen={isOpen} onClose={handleClosePrivacySettingsDialog} />
              )}
            </div>
          )}
          {mustOpenPrivacySettings && (
            <PrivacySettingsDialog isOpen={isOpen} onClose={handleClosePrivacySettingsDialog} />
          )}
        </div>
      )}
    </>
  );
};

const ErrorBoundShareProfileLinkDialog = (props: PropsFromCaller) => {
  return (
    <ErrorBoundaryWithTagging
      componentName="ShareProfileLinkDialog"
      errorComponent={
        <InlineNotification severity="error" title={_t('There has been an error')} role="alert">
          {_t('There has been an error in the share profile link dialog. Our development team will be alerted.')}
        </InlineNotification>
      }
    >
      <ShareProfileLinkDialog {...props} />
    </ErrorBoundaryWithTagging>
  );
};

export default ErrorBoundShareProfileLinkDialog;
